<template>
  <div class="apply-form__section apply-form__documents">
    <ValidationObserver>
      <v-row>
        <v-col cols="9" md="10">
          <h2>{{ $t('application.documents') }}</h2>
        </v-col>
        <v-col
          v-if="
            documentsSettings.photoValidationMode !==
            fieldValidationMode.NotPrompted
          "
          class="application-image-field"
          cols="12"
          md="2"
        >
          <ValidationProvider
            v-slot="validationData"
            name="documents_file"
            mode="eager"
            slim
            :rules="getValidationRules(documentsSettings.photoValidationMode)"
          >
            <image-input
              v-model="application.photo"
              :label="
                getLabel(
                  'application.photo',
                  documentsSettings.photoValidationMode
                )
              "
              upload-endpoint="application-file"
              download-endpoint="application-file"
              :additional-values="{ fileType: fileType.Photo }"
              :file-prefix="formId"
              :validation-data="validationData"
            />
          </ValidationProvider>
        </v-col>
      </v-row>

      <v-row v-if="localize(documentsSettings.uploadInfoText)">
        <v-col cols="12">
          <div v-html="localize(documentsSettings.uploadInfoText)" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div v-html="$t('application.documentsInfo')" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <div class="text--muted text-sm flex-grow-1">
            {{ $t('application.mandatoryFieldInfo') }}
          </div>
        </v-col>
      </v-row>

      <v-row
        v-if="
          documentsSettings.letterValidationMode !==
          fieldValidationMode.NotPrompted
        "
        class="mt-4 border-bot"
      >
        <v-col cols="12" md="6" class="d-flex align-center">
          <v-radio-group
            v-model="letterMode"
            hide-details="true"
            class="mt-0"
            :label="$t('application.letter')"
          >
            <v-radio value="upload" :label="$t('application.letterUpload')" />
            <v-radio value="text" :label="$t('application.letterAsText')" />
          </v-radio-group>
        </v-col>

        <v-col cols="12" md="6" class="d-flex align-center">
          <template v-if="letterMode === 'upload'">
            <ValidationProvider
              v-slot="validationData"
              name="letter_file"
              mode="eager"
              slim
              :custom-messages="
                getCustomFileUploadMessage(
                  $t('validation.letterFileIsMandatory')
                )
              "
              :rules="
                getLetterValidationRules(
                  documentsSettings.letterValidationMode,
                  'upload'
                )
              "
            >
              <file-input
                v-model="application.letter"
                :label="
                  getLabel(
                    'application.letter',
                    documentsSettings.letterValidationMode
                  )
                "
                :accept="allowedFileTypes"
                upload-endpoint="application-file"
                download-endpoint="application-file"
                :additional-values="{ fileType: fileType.Letter }"
                :file-prefix="formId"
                :validation-data="validationData"
                name="letter-file"
                @fileUploaded="handleFileUploaded(false)"
              />
            </ValidationProvider>
          </template>

          <template v-else-if="letterMode === 'text'">
            <ValidationProvider
              v-slot="{ errors }"
              name="letter_file"
              mode="eager"
              slim
              :rules="
                getLetterValidationRules(
                  documentsSettings.letterValidationMode,
                  'text'
                )
              "
            >
              <v-textarea
                v-model="application.letterText"
                hide-details="auto"
                solo
                flat
                :label="
                  getLabel(
                    'application.letter',
                    documentsSettings.letterValidationMode
                  )
                "
                :error-messages="errors"
              ></v-textarea>
            </ValidationProvider>
          </template>
        </v-col>
      </v-row>

      <v-row class="mt-md-0">
        <v-col
          v-if="
            documentsSettings.cvValidationMode !==
            fieldValidationMode.NotPrompted
          "
          cols="12"
          md="6"
        >
          <ValidationProvider
            v-slot="validationData"
            name="cv_file"
            mode="eager"
            slim
            :rules="getValidationRules(documentsSettings.cvValidationMode)"
            :custom-messages="
              getCustomFileUploadMessage($t('validation.cvFileIsMandatory'))
            "
          >
            <file-input
              v-model="application.cv"
              :label="
                getLabel('application.cv', documentsSettings.cvValidationMode)
              "
              :accept="allowedFileTypes"
              :upload-endpoint="'application-file'"
              :download-endpoint="'application-file'"
              :additional-values="{ fileType: fileType.CV }"
              :file-prefix="formId"
              :validation-data="validationData"
              @fileUploaded="fileUploadedHandler(true)"
            ></file-input>
          </ValidationProvider>
        </v-col>

        <v-col
          v-if="
            documentsSettings.documentsValidationMode !==
            fieldValidationMode.NotPrompted
          "
          cols="12"
          md="6"
        >
          <ValidationProvider
            v-slot="validationData"
            name="documents_file"
            mode="eager"
            slim
            :rules="
              getValidationRules(documentsSettings.documentsValidationMode)
            "
          >
            <file-input
              v-model="application.documents"
              multiple
              :label="
                getLabel(
                  'application.additionalDocuments',
                  documentsSettings.documentsValidationMode
                )
              "
              :accept="allowedFileTypes"
              :upload-endpoint="'application-file'"
              :download-endpoint="'application-file'"
              :additional-values="{ fileType: fileType.Document }"
              :file-prefix="formId"
              :validation-data="validationData"
              @fileUploaded="fileUploadedHandler(false)"
            ></file-input>
          </ValidationProvider>
        </v-col>
        <template v-if="activeAttachmentTypes && application.customAttachments">
          <v-col
            v-for="attachmentType in activeAttachmentTypes"
            cols="12"
            md="6"
            :key="attachmentType.type"
          >
            <ValidationProvider
              v-slot="validationData"
              :name="`custom_file_${attachmentType.type}`"
              mode="eager"
              slim
              :rules="getValidationRules(attachmentType.validationMode)"
            >
              <file-input
                v-model="application.customAttachments[attachmentType.type]"
                multiple
                :label="
                  getCustomLabel(
                    attachmentType.type,
                    attachmentType.validationMode
                  )
                "
                :accept="allowedFileTypes"
                :upload-endpoint="'application-file'"
                :download-endpoint="'application-file'"
                :additional-values="{
                  fileType: fileType.Document
                }"
                :file-prefix="formId"
                :validation-data="validationData"
                @fileUploaded="fileUploadedHandler(false)"
              />
            </ValidationProvider>
          </v-col>
        </template>
      </v-row>
    </ValidationObserver>

    <div class="mt-4">
      <i class="text-sm text--muted">{{ $t('application.fileSizeInfo') }}</i>
    </div>

    <cv-parser-dialog v-model="parsing"></cv-parser-dialog>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import { mapState } from 'vuex';
import { eventBus } from '@/event-bus.js';

import cvParserDialog from './cv-parser-dialog.vue';

import fileInput from '../file-input.vue';
import imageInput from '../image-input.vue';
import fileTypeEnums from '@/enum/application-file-type.js';
import validationModeEnums from '@/enum/field-validation-mode.js';

export default {
  props: {
    value: {
      type: Object,
      required: true
    },

    documentsSettings: {
      type: Object,
      required: true
    },

    jobofferId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    customAttachmentTypes: [],
    allowedFileTypes: 'image/*, .pdf, .docx, .doc, .odt',
    uploading: false,
    parsing: false,
    letterMode: 'upload'
  }),

  computed: {
    ...mapState('applicationModule', {
      formId: 'formId'
    }),

    ...mapState('companyModule', {
      slug: 'slug'
    }),

    application: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },

    cvFile() {
      return this.application.cv;
    },

    fieldValidationMode() {
      return validationModeEnums.FieldValidationModes;
    },

    fileNotEmptyRule() {
      return [
        (v) => !!v || this.$t('validation.fieldIsMandatory'),
        (v) => (v && v.length > 0) || this.$t('validation.fieldIsMandatory')
      ];
    },

    fileType() {
      return fileTypeEnums.ApplicationFileTypes;
    },

    activeAttachmentTypes() {
      return this.documentsSettings?.customAttachmentTypes?.filter(
        (x) => x.validationMode !== this.fieldValidationMode.NotPrompted
      );
    }
  },

  async mounted() {
    this.customAttachmentTypes = await this.loadTranslations({
      slug: this.slug,
      translationKey: 'attachmentTypes',
      jobofferId: this.jobofferId
    });
  },

  methods: {
    ...mapActions('applicationModule', {
      parseCvFile: 'parseCvFile'
    }),
    ...mapMutations('applicationModule', {
      setEducationData: 'setEducationData',
      setWorkData: 'setWorkData'
    }),
    ...mapActions('companyModule', {
      loadTranslations: 'loadTranslations'
    }),

    async fileUploadedHandler(isCvFile) {
      if (isCvFile && this.cvFile.length > 0) {
        this.parsing = true;

        const cvDataRequest = {
          application: this.application,
          objectId: this.cvFile[0].objectName,
          formId: this.formId,
          jobofferId: this.jobofferId
        };

        let result = null;
        try {
          result = await this.parseCvFile(cvDataRequest);
          this.setWorkData(result.workData);
          this.setEducationData(result.educationData);
          this.$emit('file-parsed', result);
        } catch {
          eventBus.$emit(
            'errorMessage',
            'Es ist ein Fehler beim Auslesen des Lebenslaufs aufgetreten'
          );
        }

        this.parsing = false;
      }

      this.$emit('change');
    },

    getLabel(labelKey, validationMode) {
      let label = this.$t(labelKey);
      if (validationMode === this.fieldValidationMode.Mandatory) {
        label += '*';
      }

      return label;
    },

    getCustomFileUploadMessage(message) {
      return {
        required: message,
        fileUploaded: message
      };
    },

    getCustomLabel(customType, validationMode) {
      const translation = this.customAttachmentTypes[customType];
      let label = this.localize(translation) ?? '';
      if (validationMode === this.fieldValidationMode.Mandatory) {
        label += '*';
      }

      return label;
    },

    getLetterValidationRules(validationMode, inputType) {
      if (
        validationMode === this.fieldValidationMode.Mandatory &&
        inputType === this.letterMode
      ) {
        return 'required|fileUploaded';
      }

      return '';
    },

    getValidationRules(validationMode) {
      if (validationMode === this.fieldValidationMode.Mandatory) {
        return 'required|fileUploaded';
      }

      return '';
    }
  },

  components: {
    cvParserDialog,
    fileInput,
    imageInput
  }
};
</script>
<style scoped>
.application-image-field {
  position: absolute;
  width: auto;
  right: 0;
  top: 5em;
}

@media only screen and (min-width: 960px) {
  .application-image-field {
    margin-top: -100px;
    position: relative;
    width: 100%;
    right: auto;
    top: 0;
  }
}
</style>
